import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Container,
  FormControl,
  Image,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  ArrowLeft,
  ArrowLeftCircleFill,
  ArrowRightCircleFill,
  Image as ImageIcon,
  Search,
  Send,
  XCircle,
} from 'react-bootstrap-icons';
import { useLocation, useSearchParams } from 'react-router-dom';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';

import './styles/styles.css';
import CloseIcon from '../../../assets/close.svg';
import DetailsIcon from '../../../assets/details.svg';
import Emojicon from '../../../assets/emoji.svg';

import UserIcon from '../../../assets/user.svg';
import { config } from '../../../modules/config';
import { MessageDeliveryStatus } from '../../../modules/enums/status';
import {
  type MessageEvent,
  useAuth,
  useMessagingHook,
  useMutationRequest,
  useNotifications,
  useRequest,
} from '../../../modules/hooks';
import type {
  GuestPackageResponse,
  MewsReservation,
  MewsReservationResponse,
} from '../../../modules/interfaces';
import {
  type MessageInboundData,
  messagingEvents,
} from '../../../modules/mappers/messagingEvents';
import { endpoints } from '../../../modules/mappers/urls';
import { normalizePhoneNumber } from '../../../modules/utils/sendMessage';
import { generateUUID, getApiErrorMessage } from '../../../modules/utils/transform';
import BookingDetails from '../../BookingDetails';
import Header from '../../Header';
import { SearchInboxInput } from '../../Inputs/Search';
import { GuestReservationTableModal } from '../../Modals/GuestReservationsModal';
import { DeliveryReceipt } from './deliveryReceipt';

type User = {
  id: string;
  name: string;
  lastMessage: string;
  lastMessageTimestamp: string;
  unread: boolean;
  reservationId: string;
  guestEmail: string;
};

type Message = {
  messageId: string;
  senderId: string;
  receiverId: string;
  text: string;
  timestamp: string;
  read: boolean;
  isRead?: boolean;
  status?: string;
  channel?: string;
  messageType: string;
  imageUrl?: string;
  imageFile?: File;
  isIncoming?: boolean;
  propertyId?: string;
};

export interface ConversationSummaryData {
  guestPhoneNumber: string;
  lastMessageSender: string;
  lastMessageText: string;
  lastMessageTimestamp: string;
  propertyName: string;
  guestName: string;
  guestEmail: string;
  reservationId: string;
}

const transformInboundDataToMessage = (inboundData: MessageInboundData): Message => {
  return {
    messageId: inboundData.messageId,
    senderId: inboundData.sender,
    receiverId: inboundData.receiver,
    text: inboundData.text,
    timestamp: inboundData.timestamp,
    read: inboundData.status === 'read',
    isRead: inboundData.status === MessageDeliveryStatus.READ,
    channel: inboundData.channel,
    messageType: inboundData.messageType,
    imageUrl: inboundData.imageUrl,
    isIncoming: inboundData.isIncoming,
    propertyId: inboundData.propertyId,
  };
};

export interface SendMessageResponse {
  messageId: string;
  status: string;
}

type LoadedMessages = {
  [userId: string]: Message[];
};

export const InboxDashboard = () => {
  const location = useLocation();
  const { credentialsInfo } = useAuth()!;
  const [showInput, setShowInput] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { setSimpleToasts } = useNotifications()!;
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(users[0] || null);
  const [loadedMessages, setLoadedMessages] = useState<LoadedMessages>({});
  const [lastSentMessage, setLastSentMessage] = useState<Message | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSendFocus, setSendFocus] = useState<boolean>(false);
  const [isEmojiOpen, setEmojiOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>('');
  const [selectedReservation, setSelectedReservation] = useState<MewsReservation | null>(
    null,
  );
  const [newMessage, setNewMessage] = useState<string>('');
  const [showSideNav, setShowSideNav] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const { selectedProperty, userPropertiesData, setSelectedProperty } =
    useContext(UserPropertiesContext)!;
  const [guestReservations, setGuestReservations] = useState<MewsReservation[]>([]);
  const [guestPackages, setGuestPackages] = useState<GuestPackageResponse[]>([]);
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [fontIdFile, setFontIdFile] = useState<File | undefined>(undefined);
  const [fromReservationDetailsFlag, setFromReservationDetailsFlag] =
    useState<boolean>(false);
  const typingTimerRef = useRef<NodeJS.Timeout>();

  const frontIdInputField = useRef<HTMLInputElement>(null);

  const doneTypingInterval = process.env.REACT_APP_TYPING_THRESHOLD_MS
    ? Number(process.env.REACT_APP_TYPING_THRESHOLD_MS)
    : 500;

  const [searchParams] = useSearchParams();
  let lastLabel: string | null = null;

  const urlGuestEmail = searchParams.get('guestEmail');

  const urlPropertyId = searchParams.get('propertyId');

  const {
    guestReservationUser,
    reservationGuestId,
    reservationGuest,
    fromReservationDetails,
    reservationPackages,
  } = location.state || {};

  const urlReservationsByEmail = `${endpoints.RESERVATION}/customer-reservations/${guestEmail}`;
  const [
    { loading: loadingReservationsByEmail, data: reservationsByEmail },
    getGuestsReservations,
  ] = useRequest<MewsReservationResponse>(
    urlReservationsByEmail,
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const checkFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024;

    if (file.size <= 0) {
      setSimpleToasts({
        type: 'danger',
        message: 'Files with a size of 0 bytes are invalid.',
        show: true,
      });
      return false;
    }

    if (file.size >= maxSize) {
      setSimpleToasts({
        type: 'danger',
        message: 'The file is too large. Maximum size is 5 MB.',
        show: true,
      });
      return false;
    }

    return true;
  };

  const handleFrontIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files?.[0] || undefined;
    if (files && checkFileSize(files)) {
      setFontIdFile(files);
    }
  };

  const handleInboundMessage = useCallback(
    (message: MessageInboundData) => {
      const incomingMessage = transformInboundDataToMessage(message);
      const selectedPropertyId = selectedProperty?.property.uuid;
      if (incomingMessage.propertyId !== selectedPropertyId) {
        return;
      }
      setLoadedMessages((prevLoadedMessages) => ({
        ...prevLoadedMessages,
        [incomingMessage.senderId]: [
          ...(prevLoadedMessages[incomingMessage.senderId] || []),
          incomingMessage,
        ],
      }));
      setUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (
            user.id === incomingMessage.senderId &&
            (!selectedUser || user.id !== selectedUser.id)
          ) {
            return { ...user, unread: true };
          }
          return user;
        }),
      );
    },
    [selectedUser],
  );

  const eventHandlers: MessageEvent<MessageInboundData>[] = useMemo(
    () => [
      {
        name: messagingEvents.MESSAGE.INBOUND,
        callback: handleInboundMessage,
      },
    ],
    [handleInboundMessage],
  );

  useMessagingHook<MessageInboundData>(messagingEvents.CHANNEL_NAME, eventHandlers);

  const messageListRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const messageContainer = messageListRef.current;
    if (messageContainer) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  };

  const propertyId = selectedProperty?.property?.uuid || '';
  const propertyPhoneNumber =
    normalizePhoneNumber(selectedProperty?.property?.phoneNumber) ||
    normalizePhoneNumber(config.messaging.property.TheCoachHouse) ||
    '';

  const isOutbound = (message: Message) => {
    const propertyNumber = normalizePhoneNumber(selectedProperty?.property?.phoneNumber);
    const configPropertyNumber = normalizePhoneNumber(
      config.messaging.property.TheCoachHouse,
    );

    if (message.isIncoming !== null && message.isIncoming !== undefined) {
      return !message.isIncoming;
    }

    return (
      message.senderId === propertyNumber || message.senderId === configPropertyNumber
    );
  };

  useEffect(() => {
    if (fromReservationDetails) {
      setFromReservationDetailsFlag(fromReservationDetails);
    }

    if (reservationPackages) {
      setGuestPackages(reservationPackages);
    }
  }, [fromReservationDetails, reservationPackages]);

  useEffect(() => {
    if (!urlPropertyId || !userPropertiesData?.userProperties) return;

    const property = userPropertiesData?.userProperties?.find(
      (item) => item.property.uuid === urlPropertyId.toString(),
    );

    if (property) {
      setSelectedProperty(property);
    }
  }, [urlPropertyId, userPropertiesData?.userProperties]);

  useEffect(() => {
    if (!guestEmail) return;

    getGuestsReservations({
      headers: {
        authorization: credentialsInfo?.token,
        'property-id': selectedProperty?.property.uuid,
      },
    });
  }, [guestEmail, selectedProperty]);

  useEffect(() => {
    if (reservationGuestId) {
      setSelectedReservationId(reservationGuestId);
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const url = `${endpoints.MESSAGING}/send-message`;
  const [{ loading, data: messageSendResult, error }, sendMessagePost] =
    useMutationRequest<SendMessageResponse>(
      url,
      'post',
      {
        authToken: credentialsInfo?.token,
        propertyId: selectedProperty?.property.uuid,
      },
      { manual: true },
    );

  useEffect(() => {
    if (error) {
      const message = getApiErrorMessage(error);
      setSimpleToasts({ type: 'danger', message, show: true });
    }

    if (messageSendResult) {
      setFontIdFile(undefined);
    }
  }, [loading, messageSendResult, error, setSimpleToasts]);

  const fetchConversationUrl = `${endpoints.MESSAGING}/conversations/${propertyId}`;
  const [
    { loading: loadingConversations, data: conversationsData, error: conversationsError },
    fetchConversations,
  ] = useRequest(
    fetchConversationUrl,
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId,
    },
    { manual: true },
  );

  const fetchConversationMessagesUrl = `${endpoints.MESSAGING}/conversations/${propertyId}/messages`;
  const [
    { loading: loadingMessages, data: messagesData, error: messagesError },
    fetchMessages,
  ] = useRequest(
    fetchConversationMessagesUrl,
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId,
    },
    { manual: true },
  );

  const transformGetConversationData = (
    apiResponse: ConversationSummaryData[],
  ): User[] => {
    const newUsers: User[] = [];
    apiResponse.forEach((conversation: ConversationSummaryData) => {
      const user: User = {
        id: conversation.guestPhoneNumber,
        name: conversation.guestName,
        lastMessage: conversation.lastMessageText,
        lastMessageTimestamp: conversation.lastMessageTimestamp,
        reservationId: conversation.reservationId,
        guestEmail: conversation.guestEmail || '',
        unread: false,
      };

      newUsers.push(user);
    });
    return newUsers;
  };

  const transformGetMessagesData = (apiResponse: MessageInboundData[]): Message[] => {
    const newMessages: Message[] = [];
    apiResponse.forEach((messageData: MessageInboundData) => {
      const transformedMessage: Message = {
        messageId: messageData.messageId,
        senderId: normalizePhoneNumber(messageData.sender) || '',
        receiverId: messageData.receiver,
        text: messageData.text,
        timestamp: messageData.timestamp,
        read: messageData.status === MessageDeliveryStatus.READ,
        isRead: messageData.status === MessageDeliveryStatus.READ,
        channel: messageData.channel,
        messageType: messageData.messageType,
        imageUrl: messageData.imageUrl,
        status: messageData.status,
        isIncoming: messageData.isIncoming,
      };
      newMessages.push(transformedMessage);
    });
    return newMessages;
  };

  useEffect(() => {
    if (selectedProperty) {
      fetchConversations({
        params: {
          limit: process.env.REACT_APP_CONVERSATIONS_LIMIT || 50,
          filter: searchTerm,
        },
        headers: {
          authorization: credentialsInfo?.token,
          'property-id': selectedProperty?.property.uuid,
        },
      });
    }
  }, [fetchConversations, selectedProperty, searchTerm]);

  const handleSelectUser = async (user: User, fromDetails?: boolean) => {
    if (!fromDetails) {
      setFromReservationDetailsFlag(false);
    }

    setIsLoading(true);
    if (loadedMessages[user.id]) {
      setLoadedMessages((prevLoadedMessages) => {
        const userMessages = prevLoadedMessages[user.id];
        let updatedMessages = userMessages.map((msg) => ({
          ...msg,
          read: true,
        }));
        if (
          lastSentMessage &&
          !userMessages.find((msg) => msg.messageId === lastSentMessage.messageId)
        ) {
          updatedMessages = [...updatedMessages, lastSentMessage];
        }
        return {
          ...prevLoadedMessages,
          [user.id]: updatedMessages,
        };
      });
    } else {
      await fetchMessages(`${fetchConversationMessagesUrl}/${user.id}`);
    }
    setGuestEmail(user.guestEmail);
    setSelectedUser(user);
    setShowConversation(true);
    setIsLoading(false);
    setUsers((prevUsers) =>
      prevUsers.map((u) => {
        if (u.id === user.id) {
          return { ...u, unread: false };
        }
        return u;
      }),
    );

    if (showSideNav) {
      setShowSideNav(false);
    }
    if (user.reservationId) {
      setSelectedReservationId(user.reservationId);
    } else {
      setSimpleToasts({
        type: 'danger',
        message: 'There is no reservation id associated with this conversation',
        show: true,
      });
    }
  };

  useEffect(() => {
    if (conversationsError) {
      const message = getApiErrorMessage(conversationsError);
      setSimpleToasts({ type: 'danger', message, show: true });
    }
    if (conversationsData) {
      const newUsers = transformGetConversationData(
        conversationsData.result as ConversationSummaryData[],
      );

      setUsers(
        newUsers.sort((a, b) =>
          a.lastMessageTimestamp > b.lastMessageTimestamp ? -1 : 1,
        ),
      );

      if (guestReservationUser && guestReservationUser?.Email) {
        const user = newUsers.find(
          (item) => item.guestEmail === guestReservationUser?.Email.toString(),
        );
        if (user) {
          handleSelectUser(user, true);

          setSelectedReservationId(reservationGuestId);
          setSelectedReservation(reservationGuest);
        }
      }

      if (urlGuestEmail) {
        const user = newUsers.find(
          (item) => item.guestEmail === urlGuestEmail.toString(),
        );

        if (user) {
          handleSelectUser(user);
        }
      }
    }
  }, [loadingConversations, conversationsData, conversationsError, setSimpleToasts]);

  useEffect(() => {
    if (!reservationsByEmail || loadingReservationsByEmail) return;

    if (reservationsByEmail.result.Reservations.length === 1) {
      setGuestReservations(reservationsByEmail.result.Reservations);
    } else {
      setGuestReservations(reservationsByEmail.result.Reservations);
    }
  }, [reservationsByEmail, loadingReservationsByEmail]);

  useEffect(() => {
    if (!selectedReservationId) return;

    if (reservationGuest && selectedReservationId === reservationGuestId) {
      setSelectedReservation(reservationGuest);
    }
  }, [selectedReservationId]);

  useEffect(() => {
    if (!reservationGuest) return;

    setSelectedReservation(reservationGuest);
  }, [reservationGuest, setSimpleToasts]);

  useEffect(() => {
    if (messagesError) {
      const message = getApiErrorMessage(messagesError);
      setSimpleToasts({ type: 'danger', message, show: true });
    }
    if (messagesData && selectedUser) {
      const messagesResult = messagesData.result as MessageInboundData[];
      const areMessagesForSelectedUser = messagesResult.some(
        (message) => message.guestPhoneNumber === selectedUser.id,
      );
      if (areMessagesForSelectedUser) {
        const newMessages: Message[] = transformGetMessagesData(
          messagesResult as MessageInboundData[],
        );

        setLoadedMessages((prevLoadedMessages) => ({
          ...prevLoadedMessages,
          [selectedUser?.id || '']: newMessages,
        }));
      }
    }
  }, [loadingMessages, messagesData, messagesError, setSimpleToasts, selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [loadedMessages, selectedUser]);

  const getLastMessageChannel = (userId: string): string => {
    const userMessages = loadedMessages[userId];
    if (userMessages && userMessages.length > 0) {
      return userMessages[userMessages.length - 1].channel || 'sms';
    }
    return 'sms'; // Return a default channel if no messages are found
  };

  const handleSendMessage = (): void => {
    if ((newMessage.trim() || fontIdFile) && selectedUser) {
      const channelToUse = getLastMessageChannel(selectedUser.id);

      const newMsg: Message = {
        messageId: generateUUID(),
        senderId: propertyPhoneNumber,
        receiverId: selectedUser.id,
        text: newMessage,
        timestamp: new Date().toISOString(),
        read: true,
        messageType: fontIdFile ? 'image' : 'text',
        channel: channelToUse,
        imageUrl: fontIdFile ? URL.createObjectURL(fontIdFile) : '',
        status: 'sent',
      };

      const formData = new FormData();

      formData.append('to', selectedUser.id || '');

      formData.append('messageType', fontIdFile ? 'image' : 'text');
      formData.append('timestamp', newMsg.timestamp);
      formData.append('propertyId', propertyId);

      formData.append('text', newMessage);

      if (fontIdFile) {
        const fileName = fontIdFile.name;
        formData.append('files', fontIdFile, fileName);
      }

      setLastSentMessage(newMsg);
      setLoadedMessages((prevLoadedMessages) => ({
        ...prevLoadedMessages,
        [selectedUser.id]: [...(prevLoadedMessages[selectedUser.id] || []), newMsg],
      }));

      setNewMessage('');

      sendMessagePost(formData);
    }
  };

  const formatMessageDateTime = (timestamp: string): string => {
    const messageDateTime = DateTime.fromISO(timestamp);
    const now = DateTime.now();

    if (messageDateTime.hasSame(now, 'day')) {
      return messageDateTime.toLocaleString(DateTime.TIME_SIMPLE);
    }
    return messageDateTime.toFormat('LLL dd, yyyy');
  };

  function formatMessageDate(dateString: string): string {
    const messageDate = new Date(dateString);
    const now = new Date();

    const msInDay = 24 * 60 * 60 * 1000;

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate(),
    );

    const diffInDays = Math.floor((today.getTime() - msgDay.getTime()) / msInDay);

    if (diffInDays === 1) {
      return 'Yesterday';
    }

    if (diffInDays > 1 && diffInDays < 7) {
      return messageDate.toLocaleDateString('en-US', { weekday: 'long' });
    }

    if (diffInDays >= 7) {
      const mm = String(messageDate.getMonth() + 1).padStart(2, '0');
      const dd = String(messageDate.getDate()).padStart(2, '0');
      const yyyy = messageDate.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }

    return 'Today';
  }

  const formatMessageTime = (timestamp: string): string => {
    const messageDateTime = DateTime.fromISO(timestamp);
    return messageDateTime.toFormat('hh:mm a');
  };

  const handleMouseEnterSend = () => {
    setSendFocus(true);
  };

  const handleMouseLeaveSend = () => {
    setSendFocus(false);
  };

  const handleBookingDetailsData = (item: MewsReservation) => {
    setSelectedReservation(item);
    setShowSideNav(true);
  };

  const handleBookingDetailsSidebar = () => {
    if (fromReservationDetailsFlag) {
      setSelectedReservation(reservationGuest);
    } else {
      setSelectedReservation(guestReservations[0]);
    }

    setShowSideNav(true);
  };

  const bookingDetailsComponent = () => {
    return selectedReservation ? (
      <BookingDetails
        selectedReservation={selectedReservation}
        fromReservationDetails={fromReservationDetailsFlag}
        reservationPackages={guestPackages}
      />
    ) : (
      <div
        style={{
          textAlign: 'center',
          marginTop: '60px',
          color: '#D17E00',
          fontSize: '20px',
        }}
      >
        No active reservation(s) found for guest.
      </div>
    );
  };

  const onEmojiClick = useCallback(() => {
    setEmojiOpen(!isEmojiOpen);
  }, [isEmojiOpen]);

  const onEmojiSelectClick = useCallback(
    (emoji: EmojiClickData) => {
      setNewMessage(newMessage + emoji.emoji);
    },
    [newMessage],
  );

  return (
    <Container id="inbox" fluid className="mainView">
      <Header title="Inbox" />
      <Container fluid>
        <Row className="flex-grow-1 overflow-hidden table-message-container">
          {/* Always render user list on non-mobile, conditionally render on mobile */}
          {(isMobile && !showConversation) || !isMobile ? (
            <Col
              xs={12}
              md={3}
              lg={3}
              className="p-0 d-flex flex-column conversations-container"
            >
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {!showInput ? (
                  <Search
                    size={20}
                    style={{
                      cursor: 'pointer',
                      marginBottom: '20px',
                      margin: '4%',
                      marginLeft: '25px',
                    }}
                    onClick={() => {
                      setShowInput(true);
                      setSearchTerm('');
                    }}
                  />
                ) : (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      transition: 'opacity 0.3s ease, transform 0.3s ease',
                      opacity: showInput ? 1 : 0,
                      transform: showInput ? 'translateX(0)' : 'translateX(-10px)',
                    }}
                  >
                    <SearchInboxInput
                      style={{
                        width: '92%',
                        marginBottom: '20px',
                        margin: '4%',
                        transition: 'all 0.3s ease',
                      }}
                      placeholder="Search guests here"
                      onBlur={() => setShowInput(false)}
                      onChange={(e) => {
                        if (typingTimerRef.current) {
                          clearTimeout(typingTimerRef.current); // Clear previous timer
                        }

                        typingTimerRef.current = setTimeout(() => {
                          const { value } = e.target as HTMLInputElement;

                          setSearchTerm(value);
                        }, doneTypingInterval);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="conversation-list-container overflow-auto">
                {users.map((user) => (
                  <div key={user.id}>
                    <div
                      onClick={() => {
                        handleSelectUser(user);
                        if (isMobile) setShowConversation(true);
                      }}
                      className={`d-block user-select-button w-100 text-start ${
                        selectedUser && selectedUser.id === user.id
                          ? 'btn-grey'
                          : 'btn-transparent'
                      }`}
                    >
                      <div className="user-item">
                        {selectedUser && selectedUser.id === user.id ? (
                          <div className="select-border" />
                        ) : (
                          <div className="un-select-border" />
                        )}
                        <img className="user_image" alt="user" src={UserIcon} />
                        <div className="user-details">
                          <div className="user-name">{user.name}</div>
                        </div>
                        <div className="user-date">
                          <div className="room-id">
                            {formatMessageDateTime(user.lastMessageTimestamp)}
                          </div>
                          {user.unread && (
                            <Badge className="badge-unread-message" pill>
                              1
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          ) : null}
          {/* Conditionally render conversation on mobile, always render on non-mobile */}

          {guestReservations.length !== 0 && (
            <GuestReservationTableModal
              selectedReservations={guestReservations}
              show={showBookingModal}
              handleClose={() => setShowBookingModal(false)}
              setConfirmedReservation={handleBookingDetailsData}
            />
          )}

          <Col
            xs={12}
            md={showSideNav && !isMobile ? 5 : 9}
            lg={showSideNav && !isMobile ? 5 : 9}
            className="p-0 d-flex flex-column messages-container"
          >
            <Col className="p-0 messages-title">
              <div className="header-chat ">
                {isMobile && showConversation && (
                  <ArrowLeft
                    style={{
                      color: 'black',
                      marginRight: '8px',
                      cursor: 'click',
                    }}
                    onClick={() => setShowConversation(false)}
                  />
                )}
                {selectedUser && (
                  <img className="user_image mr-8px " alt="user" src={UserIcon} />
                )}
                <div className="header-chat-title">
                  {selectedUser && selectedUser.name}
                </div>
              </div>
              {!showBookingModal &&
                selectedReservationId &&
                (loadingReservationsByEmail ? (
                  <div className="sidebar-button mr-8px">
                    <Spinner
                      animation="border"
                      variant="primary"
                      style={{ color: '#2BD579', marginRight: '10px' }}
                    />
                  </div>
                ) : (
                  <div
                    className="sidebar-button mr-8px"
                    onClick={() =>
                      guestReservations.length <= 1 || fromReservationDetailsFlag
                        ? handleBookingDetailsSidebar()
                        : setShowBookingModal(!showBookingModal)
                    }
                  >
                    <img alt="details " src={DetailsIcon} />
                    {!isMobile && (
                      <div className="booking-details-title">
                        {showSideNav ? 'Show Reservations' : 'Booking details'}
                      </div>
                    )}
                  </div>
                ))}
            </Col>
            {isLoading ? (
              <Spinner
                animation="border"
                variant="primary"
                className="d-block m-auto mt-4"
                style={{ color: '#2BD579' }}
              />
            ) : (
              <div
                ref={messageListRef}
                className={selectedUser ? 'message-list-container   p-3' : 'no-data-list'}
              >
                {selectedUser ? (
                  loadedMessages[selectedUser.id]?.map((message) => {
                    const currentLabel = formatMessageDate(message.timestamp);
                    const showSeparator = currentLabel !== lastLabel;

                    lastLabel = currentLabel;

                    return (
                      <>
                        {showSeparator && (
                          <Row className="justify-content-md-center loginHeader">
                            <div className="separator-line" />
                            <Badge className="status-badge">{currentLabel}</Badge>
                            <div className="separator-line" />
                          </Row>
                        )}

                        <div
                          className={`d-flex mb-2 position-relative ${
                            isOutbound(message) ? 'justify-content-end' : ''
                          }`}
                        >
                          {isOutbound(message) ? (
                            <div className="msg-text" style={{ marginRight: 5 }}>
                              {formatMessageTime(message.timestamp)}
                            </div>
                          ) : (
                            <div className="inbound-corner" />
                          )}

                          <div
                            className={`message-bubble ${
                              isOutbound(message) ? 'outbound' : 'inbound'
                            }`}
                          >
                            {message.messageType === 'text' && (
                              <div className="message-title">{message.text}</div>
                            )}
                            {message.messageType === 'image' && message.imageUrl && (
                              <>
                                <div className="message-image-container">
                                  <img alt="Message attachment" src={message.imageUrl} />
                                </div>
                                {message.text && (
                                  <div
                                    className="message-title"
                                    style={{
                                      marginTop: '8px',
                                    }}
                                  >
                                    {message.text}
                                  </div>
                                )}
                              </>
                            )}
                            {isOutbound(message) ? (
                              <div className="msg-receipt">
                                <DeliveryReceipt
                                  status={message.status}
                                  read={message.isRead}
                                />
                              </div>
                            ) : null}
                          </div>
                          {!isOutbound(message) ? (
                            <div className="msg-text" style={{ marginLeft: 5 }}>
                              {formatMessageTime(message.timestamp)}
                            </div>
                          ) : (
                            <div className="outboud-corner" />
                          )}
                        </div>
                      </>
                    );
                  })
                ) : (
                  <div className="message-empty-message">
                    Select a conversation from the list on the left
                  </div>
                )}
              </div>
            )}
            <div
              className="bottom-send-container"
              style={{ marginBottom: !isMobile ? '8px' : '0px' }}
            >
              {
                <Col
                  style={{
                    marginBottom: '5px',
                    position: 'relative',
                    display: 'inline-block',
                  }}
                >
                  {fontIdFile && (
                    <>
                      <Image
                        alt=""
                        src={URL.createObjectURL(fontIdFile)}
                        width="60"
                        height="50"
                        style={{ marginTop: '0px' }}
                        className="ag-cell-img"
                      />

                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => setFontIdFile(undefined)}
                        style={{
                          position: 'absolute',
                          top: '-7px',
                          right: '4px',
                          borderRadius: '100%',
                          padding: '0px 3px 2px 3px',
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                        }}
                      >
                        <XCircle size={14} />
                      </Button>
                    </>
                  )}
                </Col>
              }
              <FormControl
                as="textarea"
                rows={2}
                placeholder="Type a message…"
                className="send-input-container"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />

              <div className="bottom-input-container">
                <div
                  className="fee-expand-btn"
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                  onClick={onEmojiClick}
                >
                  <img alt="emoji" src={Emojicon} />
                </div>

                <input
                  onChange={handleFrontIdChange}
                  multiple={false}
                  ref={frontIdInputField}
                  type="file"
                  accept=".jpg, .jpeg, .png, .gif"
                  hidden
                />
                <Container
                  onClick={() => frontIdInputField.current?.click()}
                  style={{
                    marginLeft: '5px',
                    marginTop: '0px',
                    padding: '0px',
                    cursor: 'pointer',
                  }}
                >
                  <ImageIcon size={20} />
                </Container>

                <div
                  onClick={handleSendMessage}
                  onMouseEnter={handleMouseEnterSend}
                  onMouseLeave={handleMouseLeaveSend}
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    cursor: 'pointer',
                  }}
                >
                  <Send color={isSendFocus ? '#15cf5f' : '#222222'} size={20} />
                </div>
              </div>
            </div>

            {isEmojiOpen && (
              <EmojiPicker
                searchDisabled
                width="100%"
                style={{
                  borderRadius: '0px',

                  minHeight: '250px',
                  maxHeight: '250px',
                }}
                previewConfig={{ showPreview: false }}
                onEmojiClick={onEmojiSelectClick}
                className="emoji-picker"
              />
            )}
          </Col>

          {!isMobile && (
            <Col
              md={4}
              lg={4}
              className={`border-left reservation-container ${
                showSideNav ? 'show-side-nav' : 'hide-side-nav'
              }`}
            >
              <div className="sidebar-title">
                <div
                  className="sidebar-button"
                  onClick={() => setShowSideNav(!showSideNav)}
                >
                  <ArrowRightCircleFill size={25} style={{ color: 'black' }} />
                </div>
                <div style={{ cursor: 'pointer' }} onClick={() => setShowSideNav(false)}>
                  <img alt="details" src={CloseIcon} />
                </div>
              </div>

              {loadingReservationsByEmail || loadingConversations ? (
                <Spinner
                  animation="border"
                  variant="primary"
                  className="d-block m-auto mt-4"
                />
              ) : (
                bookingDetailsComponent()
              )}
            </Col>
          )}
          {isMobile && (
            <div
              className={`border-left reservation-container float ${
                showSideNav ? 'show-side-nav' : 'hide-side-nav'
              }`}
            >
              <div className="sidebar-title">
                <div
                  className="sidebar-button"
                  onClick={() => setShowSideNav(!showSideNav)}
                >
                  <ArrowLeftCircleFill size={25} style={{ color: 'black' }} />
                </div>
                <div style={{ cursor: 'pointer' }} onClick={() => setShowSideNav(false)}>
                  <img alt="details" src={CloseIcon} />
                </div>
              </div>

              {loadingReservationsByEmail || loadingConversations ? (
                <Spinner
                  animation="border"
                  variant="primary"
                  className="d-block m-auto mt-4"
                />
              ) : (
                selectedReservation && (
                  <BookingDetails
                    selectedReservation={selectedReservation}
                    fromReservationDetails={fromReservationDetailsFlag}
                    reservationPackages={guestPackages}
                  />
                )
              )}
            </div>
          )}
        </Row>
      </Container>
    </Container>
  );
};
